import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Thermometer,
  Shield,
  Clock,
  FileCheck2,
  CheckCircle2,
  Phone,
  MessageCircle,
} from "lucide-react";

const services = [
  {
    name: "Instalação Profissional",
    description: "Instalação especializada com equipe certificada, garantia estendida e suporte técnico completo para todos os tipos de ambiente.",
    icon: Thermometer,
    gradient: "from-blue-500 to-cyan-400",
    benefits: [
      "Equipe técnica certificada",
      "Garantia estendida de serviço",
      "Materiais de primeira qualidade",
      "Instalação conforme normas ABNT",
      "Testes completos de funcionamento",
      "Orientação detalhada de uso",
    ],
  },
  {
    name: "Manutenção Preventiva",
    description: "Programa completo de manutenção preventiva para maximizar a eficiência energética e prolongar a vida útil dos equipamentos.",
    icon: Shield,
    gradient: "from-cyan-500 to-blue-400",
    benefits: [
      "Limpeza completa do sistema",
      "Verificação de todos os componentes",
      "Medição precisa de temperatura",
      "Análise de consumo energético",
      "Prevenção de problemas futuros",
      "Relatório técnico detalhado",
    ],
  },
  {
    name: "Suporte Técnico 24/7",
    description: "Atendimento de emergência disponível 24 horas por dia, 7 dias por semana, com técnicos especializados prontos para atender.",
    icon: Clock,
    gradient: "from-blue-600 to-indigo-500",
    benefits: [
      "Atendimento 24 horas por dia",
      "Técnicos especializados",
      "Diagnóstico rápido e preciso",
      "Reparo de componentes",
      "Substituição de peças originais",
      "Garantia total do serviço",
    ],
  },
  {
    name: "PMOC Certificado",
    description: "Plano de Manutenção, Operação e Controle totalmente em conformidade com as normas ANVISA e legislação vigente.",
    icon: FileCheck2,
    gradient: "from-indigo-500 to-purple-500",
    benefits: [
      "Conformidade com ANVISA",
      "Documentação completa",
      "Vistorias periódicas programadas",
      "Relatórios técnicos oficiais",
      "Recomendações de melhorias",
      "Suporte especializado contínuo",
    ],
  },
];

export function Services() {
  return (
    <>
      <Helmet>
        <title>Serviços | Arcgel</title>
        <meta
          name="description"
          content="Conheça nossos serviços especializados em climatização: instalação, manutenção preventiva, manutenção corretiva e PMOC."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="container relative z-10 text-center px-4 pt-20 md:pt-28">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6">
              <span className="text-white drop-shadow-2xl">
                Nossos
              </span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent ml-3">
                Serviços
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              Soluções completas em climatização com tecnologia de ponta e 
              <span className="text-cyan-300 font-semibold"> atendimento personalizado</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contato"
                className="group relative px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full shadow-xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3"
              >
                <Phone className="w-5 h-5" />
                Solicitar Orçamento
              </Link>
              
              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Serviços <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Especializados</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Conte com nossa experiência e profissionalismo para garantir o melhor em climatização
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.name}
                  className="group relative"
                >
                  <div className="relative h-full p-8 rounded-2xl border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10">
                    <div className="mb-6">
                      <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${service.gradient} shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors">
                      {service.name}
                    </h3>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-white mb-4">
                        Benefícios Inclusos
                      </h4>
                      <ul className="space-y-3">
                        {service.benefits.map((benefit) => (
                          <li
                            key={benefit}
                            className="flex items-center text-slate-300"
                          >
                            <CheckCircle2 className="mr-3 h-5 w-5 text-cyan-400 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <a
                      href={`https://wa.me/5513991028221?text=${encodeURIComponent(`Olá! Gostaria de solicitar um orçamento para o serviço: ${service.name}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative z-20 inline-flex items-center gap-2 px-5 py-2.5 bg-transparent text-white font-semibold rounded-full border border-white/40 hover:bg-green-600/30 hover:border-green-400/70 transition-all duration-300 cursor-pointer"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Solicitar Orçamento
                    </a>

                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Precisa de algum dos nossos
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Serviços Especializados?
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed">
              Entre em contato para um orçamento personalizado e descubra como podemos ajudar você
            </p>

            <div className="flex flex-wrap justify-center gap-6 mb-12">
              {[
                { icon: CheckCircle2, text: "Orçamento Gratuito" },
                { icon: Shield, text: "Garantia Estendida" },
                { icon: Clock, text: "Atendimento 24h" }
              ].map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="flex items-center gap-3 px-4 py-2 rounded-full border border-white/20">
                    <IconComponent className="w-5 h-5 text-cyan-400" />
                    <span className="text-white font-medium">{benefit.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/contato"
                className="group relative px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-full shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <Phone className="w-6 h-6" />
                Solicitar Orçamento
                
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
              
              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-10 py-5 bg-white/10 backdrop-blur-sm text-white font-bold text-lg rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <MessageCircle className="w-6 h-6" />
                WhatsApp Direto
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
